/**
 * ComposioToolkits — the SaaS app allowlist (E39).
 *
 * The Composio Tool Router can reach ~1,089 SaaS apps. This page is the
 * gate: an app is invisible and uncallable until someone approves it
 * here. Before this existed every approval was a hand-written psql
 * UPDATE on prod — no record of who approved what, and routine
 * onboarding became an engineering ticket, which defeats the point of
 * having a router at all.
 *
 * Two deliberate pieces of friction, both load-bearing:
 *
 *  1. **Shared-identity apps sit behind an explicit acknowledgement.**
 *     Some Composio apps only support auth modes where every Stanford
 *     caller reaches the provider as the SAME account, so per-user data
 *     separation does not exist. That is the ServiceNow finding of
 *     2026-06-02. The API returns 409 without the acknowledgement; this
 *     page surfaces the danger before the click rather than after it.
 *  2. **Blocking writes a row instead of deleting one.** A blocked app
 *     keeps its reason, so the decision survives. Deleting leaves a gap
 *     the next person fills by accident.
 *
 * Styling follows `Connectors.tsx`: inline styles over CSS variables,
 * since this codebase has no utility-class vocabulary for cards.
 */
import { useMemo, useState, type CSSProperties, type JSX, type ReactNode } from 'react';
import {
  AlertTriangle,
  Ban,
  Check,
  KeyRound,
  Plus,
  Search,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  useApproveComposioToolkit,
  useBlockComposioToolkit,
  useComposioAvailable,
  useComposioToolkits,
  type ComposioAvailableRow,
  type ComposioToolkitRow,
} from '@/api/index.js';
import { Button } from '@/components/ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';
import { Textarea } from '@/components/ui/textarea.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { SectionCard } from '@/components/console/SectionCard.js';
import { AsyncShell } from '@/components/console/StateBlocks.js';

type TabId = 'approved' | 'blocked' | 'browse';

/**
 * Browse-tab render cap. Composio ships ~1,089 toolkits; rendering all of
 * them makes the page crawl, and an operator scrolling past 60 unfamiliar
 * apps is not finding anything — they should search instead.
 */
const BROWSE_LIMIT = 60;

const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
  gap: 16,
};

const cardStyle: CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  background: 'var(--card-bg, white)',
};

const metaStyle: CSSProperties = {
  margin: 0,
  fontSize: 12,
  color: 'var(--muted-foreground, #64748b)',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
};

const codeStyle: CSSProperties = { fontSize: 12, color: 'var(--muted-foreground, #64748b)' };

/** Comma/newline separated free text → trimmed list, or null if empty. */
function parseList(raw: string): string[] | null {
  const items = raw
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
  return items.length > 0 ? items : null;
}

function Pill({ tone, children }: { tone: 'ok' | 'bad'; children: ReactNode }): JSX.Element {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 11,
        fontWeight: 600,
        color: tone === 'ok' ? '#15803d' : '#b91c1c',
        background: tone === 'ok' ? '#dcfce7' : '#fee2e2',
        padding: '2px 8px',
        borderRadius: 999,
      }}
    >
      {children}
    </span>
  );
}

export default function ComposioToolkits(): JSX.Element {
  const [tab, setTab] = useState<TabId>('approved');
  const [q, setQ] = useState('');
  const allowlist = useComposioToolkits();
  // Composio's full catalog costs ~11 upstream page fetches, so it is
  // only pulled once the operator actually opens the browse tab.
  const available = useComposioAvailable(tab === 'browse');
  const approve = useApproveComposioToolkit();
  const block = useBlockComposioToolkit();

  const [editing, setEditing] = useState<{
    slug: string;
    displayName: string;
    sharedIdentity: boolean;
    authSchemes: string[];
    existing?: ComposioToolkitRow;
  } | null>(null);
  const [form, setForm] = useState({ wgs: '', toolAllow: '', toolDeny: '', notes: '', ack: false });

  const rows = useMemo(() => allowlist.data?.items ?? [], [allowlist.data]);
  const approved = useMemo(() => rows.filter((r) => r.status === 'approved'), [rows]);
  const blocked = useMemo(() => rows.filter((r) => r.status === 'blocked'), [rows]);

  const needle = q.trim().toLowerCase();

  const availableRows = useMemo(() => {
    const undecided = (available.data?.items ?? []).filter((i) => i.status === 'unapproved');
    const matched = needle
      ? undecided.filter(
          (i) => i.toolkit_slug.includes(needle) || i.display_name.toLowerCase().includes(needle),
        )
      : undecided;
    return { shown: matched.slice(0, BROWSE_LIMIT), total: matched.length };
  }, [available.data, needle]);

  const listed = tab === 'approved' ? approved : blocked;
  const filteredListed = useMemo(() => {
    if (!needle) return listed;
    return listed.filter(
      (r) => r.toolkit_slug.includes(needle) || r.display_name.toLowerCase().includes(needle),
    );
  }, [listed, needle]);

  function openApprove(row: ComposioAvailableRow | ComposioToolkitRow): void {
    const fromCatalog = 'shared_identity_only' in row;
    const existing = fromCatalog ? undefined : (row as ComposioToolkitRow);
    setEditing({
      slug: row.toolkit_slug,
      displayName: row.display_name,
      sharedIdentity: fromCatalog ? (row as ComposioAvailableRow).shared_identity_only : false,
      authSchemes: fromCatalog ? (row as ComposioAvailableRow).auth_schemes : [],
      existing,
    });
    setForm({
      wgs: existing?.allowed_wg_ids.join(', ') ?? '',
      toolAllow: existing?.tool_allow?.join(', ') ?? '',
      toolDeny: existing?.tool_deny?.join(', ') ?? '',
      notes: existing?.notes ?? '',
      ack: false,
    });
  }

  function submitApprove(): void {
    if (!editing) return;
    const toolAllow = parseList(form.toolAllow);
    const toolDeny = parseList(form.toolDeny);
    if (toolAllow && toolDeny) {
      // Composio accepts one shape per toolkit; honouring one of two
      // conflicting lists would silently mis-enforce policy. The API and
      // a DB constraint both reject it — caught here for a clearer message.
      toast.error('Use an allow list or a deny list, not both.');
      return;
    }
    const name = editing.displayName;
    approve.mutate(
      {
        slug: editing.slug,
        display_name: name,
        allowed_wg_ids: parseList(form.wgs) ?? [],
        tool_allow: toolAllow,
        tool_deny: toolDeny,
        notes: form.notes.trim() || null,
        acknowledge_shared_identity: form.ack || undefined,
      },
      {
        onSuccess: () => {
          toast.success(`${name} approved`, {
            description: 'Takes effect on each user’s next request — no restart needed.',
          });
          setEditing(null);
        },
        onError: (e: unknown) => {
          const msg = e instanceof Error ? e.message : String(e);
          toast.error(
            /shared_identity/.test(msg)
              ? 'This app has no per-user login — tick the acknowledgement to proceed.'
              : `Approve failed: ${msg}`,
          );
        },
      },
    );
  }

  function doBlock(slug: string, name: string): void {
    block.mutate(
      { slug, notes: `Blocked from the admin console on ${new Date().toISOString()}.` },
      {
        onSuccess: () =>
          toast.success(`${name} blocked`, {
            description:
              'Users lose access on their next request. The row is kept with its reason.',
          }),
        onError: (e: unknown) =>
          toast.error(`Block failed: ${e instanceof Error ? e.message : String(e)}`),
      },
    );
  }

  const tabs: ReadonlyArray<readonly [TabId, string]> = [
    ['approved', `Approved (${approved.length})`],
    ['blocked', `Blocked (${blocked.length})`],
    ['browse', 'Add an app'],
  ];

  const stats: ReadonlyArray<readonly [string, string | number, string]> = [
    ['Approved', approved.length, 'reachable by entitled users'],
    ['Blocked', blocked.length, 'refused, with the reason kept'],
    [
      'Available at Composio',
      available.data ? available.data.items.length : '~1,089',
      'one row each to approve',
    ],
  ];

  return (
    <>
      <PageHeader
        eyebrow="Govern"
        title="SaaS App Allowlist"
        description="Composio can reach roughly 1,089 SaaS apps. Each stays invisible until approved here. Changes take effect on a user’s next request — no deploy, no restart."
      />

      <SectionCard
        icon={ShieldCheck}
        title="Why nothing is enabled by default"
        text="Some Composio apps only offer a shared login, where every Stanford user reaches the provider as the same account — anyone with the connector could read everyone else’s data there. That is the ServiceNow finding from June 2026. Approving one app at a time keeps that judgement explicit and on the record."
      >
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          {stats.map(([label, value, hint]) => (
            <div key={label}>
              <div style={{ fontSize: 24, fontWeight: 600 }}>
                {typeof value === 'number' ? value.toLocaleString() : value}
              </div>
              <div style={{ fontSize: 12, fontWeight: 600 }}>{label}</div>
              <div style={{ fontSize: 11, color: 'var(--muted-foreground, #64748b)' }}>{hint}</div>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard icon={Users} title="Apps" text="">
        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          {tabs.map(([id, label]) => (
            <Button
              key={id}
              size="sm"
              variant={tab === id ? 'default' : 'outline'}
              onClick={() => setTab(id)}
              aria-pressed={tab === id}
            >
              {label}
            </Button>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
            <Search size={14} aria-hidden />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={tab === 'browse' ? 'Search 1,089 apps…' : 'Filter…'}
              aria-label="Search apps"
              style={{ width: 240 }}
            />
          </div>
        </div>

        {tab === 'browse' ? (
          <AsyncShell
            isLoading={available.isLoading}
            isError={available.isError}
            error={available.error}
            isEmpty={!available.isLoading && !available.isError && availableRows.shown.length === 0}
            loadingTitle="Loading Composio’s catalog"
            errorTitle="Could not reach Composio"
            errorHint="Check COMPOSIO_API_KEY on the control-plane, and that Composio is reachable."
            emptyTitle={q ? 'No undecided apps match that search' : 'Every app has been decided'}
            emptyHint={
              q
                ? 'Search by the slug Composio uses, e.g. “googlesheets” or “quickbooks”.'
                : 'Nothing in Composio’s catalog is left without an approve or block decision.'
            }
          >
            <div>
              {availableRows.total > availableRows.shown.length ? (
                <p style={{ ...metaStyle, marginBottom: 12 }}>
                  Showing {availableRows.shown.length} of {availableRows.total.toLocaleString()}{' '}
                  undecided apps — search to narrow.
                </p>
              ) : null}
              <div style={gridStyle}>
                {availableRows.shown.map((row) => (
                  <article key={row.toolkit_slug} style={cardStyle}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{row.display_name}</h3>
                    <code style={codeStyle}>{row.toolkit_slug}</code>
                    {row.shared_identity_only ? (
                      <p style={{ ...metaStyle, color: '#b91c1c' }}>
                        <AlertTriangle size={13} aria-hidden />
                        Shared login only ({row.auth_schemes.join(', ')})
                      </p>
                    ) : (
                      <p style={metaStyle}>
                        <KeyRound size={13} aria-hidden />
                        Per-user sign-in
                        {row.auth_schemes.length > 0 ? ` (${row.auth_schemes.join(', ')})` : ''}
                      </p>
                    )}
                    <div style={{ marginTop: 'auto', paddingTop: 8 }}>
                      <Button size="sm" onClick={() => openApprove(row)}>
                        <Plus size={14} aria-hidden /> Approve
                      </Button>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </AsyncShell>
        ) : (
          <AsyncShell
            isLoading={allowlist.isLoading}
            isError={allowlist.isError}
            error={allowlist.error}
            isEmpty={!allowlist.isLoading && !allowlist.isError && filteredListed.length === 0}
            loadingTitle="Loading allowlist"
            errorTitle="Could not load the allowlist"
            errorHint="This page needs the mcp:admin scope."
            emptyTitle={tab === 'approved' ? 'No apps approved yet' : 'Nothing blocked'}
            emptyHint={
              tab === 'approved'
                ? 'Until an app is approved, no SaaS tool is reachable through the gateway. Open “Add an app” to approve one.'
                : 'Blocked apps are kept with their reason so the decision survives. Nothing has been blocked so far.'
            }
          >
            <div style={gridStyle}>
              {filteredListed.map((row) => (
                <article key={row.toolkit_slug} style={cardStyle}>
                  <header style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, flex: 1 }}>
                      {row.display_name}
                    </h3>
                    {row.status === 'approved' ? (
                      <Pill tone="ok">
                        <Check size={11} aria-hidden /> Approved
                      </Pill>
                    ) : (
                      <Pill tone="bad">
                        <Ban size={11} aria-hidden /> Blocked
                      </Pill>
                    )}
                  </header>

                  <code style={codeStyle}>{row.toolkit_slug}</code>

                  <p style={metaStyle}>
                    <Users size={13} aria-hidden />
                    {row.allowed_wg_ids.length === 0
                      ? 'Any signed-in user'
                      : row.allowed_wg_ids.join(', ')}
                  </p>

                  {row.tool_allow?.length ? (
                    <p style={metaStyle}>Only: {row.tool_allow.join(', ')}</p>
                  ) : null}
                  {row.tool_deny?.length ? (
                    <p style={metaStyle}>Blocked tools: {row.tool_deny.join(', ')}</p>
                  ) : null}

                  {row.auth_config_id ? (
                    <p style={metaStyle}>
                      <KeyRound size={13} aria-hidden />
                      Existing user logins preserved
                    </p>
                  ) : null}

                  {row.notes ? (
                    <p
                      style={{
                        margin: 0,
                        fontSize: 12,
                        lineHeight: 1.5,
                        padding: 8,
                        borderRadius: 6,
                        background: 'var(--muted, #f8fafc)',
                      }}
                    >
                      {row.notes}
                    </p>
                  ) : null}

                  {row.approved_by ? (
                    <p style={metaStyle}>
                      {row.status === 'approved' ? 'Approved' : 'Set'} by {row.approved_by}
                    </p>
                  ) : null}

                  <div style={{ display: 'flex', gap: 8, marginTop: 'auto', paddingTop: 8 }}>
                    <Button size="sm" variant="outline" onClick={() => openApprove(row)}>
                      {row.status === 'approved' ? 'Edit access' : 'Approve'}
                    </Button>
                    {row.status === 'approved' ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => doBlock(row.toolkit_slug, row.display_name)}
                        disabled={block.isPending}
                      >
                        <Ban size={14} aria-hidden /> Block
                      </Button>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          </AsyncShell>
        )}
      </SectionCard>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing?.existing ? 'Edit access' : 'Approve'} — {editing?.displayName}
            </DialogTitle>
            <DialogDescription>
              Leave a field blank to leave it unrestricted. Changes apply on each user’s next
              request.
            </DialogDescription>
          </DialogHeader>

          {editing?.sharedIdentity ? (
            <div
              style={{
                border: '1px solid #fecaca',
                background: '#fef2f2',
                borderRadius: 8,
                padding: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <strong
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  color: '#b91c1c',
                  fontSize: 13,
                }}
              >
                <AlertTriangle size={14} aria-hidden /> This app has no per-user login
              </strong>
              <p style={{ margin: 0, fontSize: 12, lineHeight: 1.55 }}>
                It only supports {editing.authSchemes.join(', ')}, so every Stanford user reaches it
                as the <em>same</em> account. Anyone able to use this connector could read and
                change everyone else’s data there. This is what happened with ServiceNow in June
                2026.
              </p>
              <label style={{ display: 'flex', gap: 8, fontSize: 12, alignItems: 'flex-start' }}>
                <input
                  type="checkbox"
                  checked={form.ack}
                  onChange={(e) => setForm({ ...form, ack: e.target.checked })}
                />
                I understand there is no per-user separation and accept that exposure.
              </label>
            </div>
          ) : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <Label htmlFor="wgs">Workgroups allowed</Label>
              <Input
                id="wgs"
                value={form.wgs}
                onChange={(e) => setForm({ ...form, wgs: e.target.value })}
                placeholder="mcp:fms-team, mcp:research-users"
              />
              <p style={metaStyle}>Blank means any signed-in Stanford user.</p>
            </div>

            <div>
              <Label htmlFor="allow">Only allow these tools</Label>
              <Input
                id="allow"
                value={form.toolAllow}
                onChange={(e) => setForm({ ...form, toolAllow: e.target.value })}
                placeholder="GMAIL_FETCH_EMAILS, GMAIL_GET_PROFILE"
              />
              <p style={metaStyle}>Blank means every tool in the app.</p>
            </div>

            <div>
              <Label htmlFor="deny">…or block these tools</Label>
              <Input
                id="deny"
                value={form.toolDeny}
                onChange={(e) => setForm({ ...form, toolDeny: e.target.value })}
                placeholder="GITHUB_DELETE_A_REPOSITORY"
              />
              <p style={metaStyle}>Use one list or the other, not both.</p>
            </div>

            <div>
              <Label htmlFor="notes">Why (kept on the row)</Label>
              <Textarea
                id="notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Requested by the finance team for invoice lookups."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              onClick={submitApprove}
              disabled={approve.isPending || (editing?.sharedIdentity === true && !form.ack)}
            >
              <Check size={14} aria-hidden />{' '}
              {approve.isPending ? 'Saving…' : editing?.existing ? 'Save' : 'Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
